import { useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Box,
  Divider,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import NotificationsRoundedIcon from '@mui/icons-material/NotificationsRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import AccountCircleRoundedIcon from '@mui/icons-material/AccountCircleRounded';
import { Sidebar, DRAWER_WIDTH } from './Sidebar';
import { useAuth } from '../../hooks/useAuth';

const ROUTE_LABELS: Record<string, string> = {
  admin:     'Admin',
  dashboard: 'Dashboard',
  tenants:   'Tenants',
  plans:     'Plans',
  tenant:    'Tenant',
  employees: 'Employees',
  customers: 'Customers',
  geofences: 'Geofences',
  visits:    'Visits',
  reports:   'Reports',
  manager:   'Manager',
  map:       'Live Map',
  alerts:    'Tracking Alerts',
  team:      'My Team',
};

const PAGE_SUBTITLES: Record<string, string> = {
  '/admin/dashboard':  'Platform overview and performance metrics',
  '/admin/tenants':    'Manage company accounts and subscriptions',
  '/admin/plans':      'Configure subscription plans and pricing',
  '/tenant/dashboard': 'Your organisation metrics and team overview',
  '/tenant/employees': 'Manage your field force team',
  '/tenant/customers': 'Customer directory and locations',
  '/tenant/geofences': 'Geofence zones and boundaries',
  '/tenant/visits':    'Schedule and track customer visits',
  '/tenant/reports':   'Activity and performance reports',
  '/manager/dashboard':'Your team\'s daily performance',
  '/manager/team':     'Field team members and status',
  '/manager/map':      'Real-time employee tracking map',
  '/manager/visits':   'Visit management and scheduling',
  '/manager/alerts':   'GPS and tracking alert notifications',
};

export function AppLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl,   setAnchorEl]   = useState<null | HTMLElement>(null);
  const { user, logout }            = useAuth();
  const location                    = useLocation();
  const navigate                    = useNavigate();

  const pathParts = location.pathname.split('/').filter(Boolean);
  const pageLabel = pathParts.map((p) => ROUTE_LABELS[p] ?? p.charAt(0).toUpperCase() + p.slice(1)).pop() ?? 'Dashboard';
  const pageSubtitle = PAGE_SUBTITLES[location.pathname] ?? '';

  const userInitials = user?.name
    ?.split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2) ?? '?';

  const handleLogout = async () => {
    setAnchorEl(null);
    await logout();
    navigate('/login');
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Sidebar mobileOpen={mobileOpen} onMobileClose={() => setMobileOpen(false)} />

      <Box
        component="main"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          minWidth: 0,
          width: { md: `calc(100% - ${DRAWER_WIDTH}px)` },
        }}
      >
        {/* ── Top Bar ─────────────────────────────────────── */}
        <AppBar
          position="sticky"
          elevation={0}
          sx={{
            bgcolor: 'background.paper',
            borderBottom: '1px solid',
            borderColor: 'divider',
            zIndex: 1100,
          }}
        >
          <Toolbar
            sx={{
              minHeight: '64px !important',
              px: { xs: 2, sm: 3 },
              gap: 2,
            }}
          >
            {/* Mobile menu */}
            <IconButton
              onClick={() => setMobileOpen(true)}
              size="small"
              sx={{ display: { md: 'none' }, mr: 0.5 }}
            >
              <MenuRoundedIcon fontSize="small" />
            </IconButton>

            {/* Page title */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  lineHeight: 1.3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {pageLabel}
              </Typography>
              {pageSubtitle && (
                <Typography
                  variant="caption"
                  sx={{
                    color: 'text.secondary',
                    display: { xs: 'none', sm: 'block' },
                    lineHeight: 1.2,
                  }}
                >
                  {pageSubtitle}
                </Typography>
              )}
            </Box>

            {/* Right actions */}
            <Stack direction="row" alignItems="center" spacing={0.5}>
              {/* Notifications */}
              <Tooltip title="Notifications">
                <IconButton
                  size="small"
                  aria-label="Notifications"
                  sx={{
                    color: 'text.secondary',
                    bgcolor: 'transparent',
                    '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.06) },
                  }}
                >
                  <NotificationsRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>

              {/* Divider */}
              <Box sx={{ width: '1px', height: 28, bgcolor: 'divider', mx: 1, flexShrink: 0 }} />

              {/* User */}
              <Tooltip title="Account">
                <Box
                  onClick={(e) => setAnchorEl(e.currentTarget)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1,
                    py: 0.5,
                    borderRadius: '10px',
                    cursor: 'pointer',
                    transition: 'background 0.15s',
                    '&:hover': { bgcolor: (t) => alpha(t.palette.primary.main, 0.06) },
                  }}
                >
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      background: 'linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)',
                    }}
                  >
                    {userInitials}
                  </Avatar>
                  <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                    <Typography
                      sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.2 }}
                    >
                      {user?.name ?? 'User'}
                    </Typography>
                    <Typography
                      sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.2 }}
                    >
                      {user?.role?.replace(/_/g, ' ')}
                    </Typography>
                  </Box>
                </Box>
              </Tooltip>
            </Stack>
          </Toolbar>
        </AppBar>

        {/* ── User menu ───────────────────────────────────── */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          transformOrigin={{ horizontal: 'right', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          PaperProps={{
            elevation: 0,
            sx: {
              mt: 1,
              minWidth: 220,
              borderRadius: '14px',
              border: '1px solid',
              borderColor: 'divider',
              boxShadow: '0 8px 32px rgba(15,23,42,0.14)',
              overflow: 'visible',
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: -6,
                right: 18,
                width: 12,
                height: 12,
                bgcolor: 'background.paper',
                transform: 'rotate(45deg)',
                borderTop: '1px solid',
                borderLeft: '1px solid',
                borderColor: 'divider',
              },
            },
          }}
        >
          <Box sx={{ px: 2.5, pt: 2, pb: 1.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'text.primary' }}>
              {user?.name}
            </Typography>
            <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>
              {user?.email}
            </Typography>
            <Box
              component="span"
              sx={{
                display: 'inline-block',
                mt: 0.75,
                px: '8px',
                py: '2px',
                borderRadius: '6px',
                bgcolor: (t) => alpha(t.palette.primary.main, 0.08),
                color: 'primary.dark',
                fontSize: '0.7rem',
                fontWeight: 700,
                letterSpacing: '0.04em',
              }}
            >
              {user?.role?.replace(/_/g, ' ')}
            </Box>
          </Box>

          <Divider />

          <Box sx={{ px: 1, py: 0.75 }}>
            <MenuItem
              onClick={() => setAnchorEl(null)}
              sx={{ borderRadius: '10px', px: 1.5, py: 1, fontSize: '0.875rem', fontWeight: 500 }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <AccountCircleRoundedIcon fontSize="small" color="action" />
              </ListItemIcon>
              Account settings
            </MenuItem>
          </Box>

          <Divider />

          <Box sx={{ px: 1, pt: 0.75, pb: 1 }}>
            <MenuItem
              onClick={handleLogout}
              sx={{
                borderRadius: '10px',
                px: 1.5,
                py: 1,
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'error.main',
                '&:hover': { bgcolor: (t) => alpha(t.palette.error.main, 0.06) },
              }}
            >
              <ListItemIcon sx={{ minWidth: 32 }}>
                <LogoutRoundedIcon fontSize="small" color="error" />
              </ListItemIcon>
              Sign out
            </MenuItem>
          </Box>
        </Menu>

        {/* ── Page content ────────────────────────────────── */}
        <Box
          sx={{
            flex: 1,
            p: { xs: 2, sm: 3 },
            overflow: 'auto',
          }}
        >
          <Outlet />
        </Box>
      </Box>
    </Box>
  );
}
